import React, { useState, useEffect } from "react";
import { makeStyles } from '@material-ui/styles';

import { withRouter } from 'react-router-dom';
import withShowToast from '../../../hocs/with_show_toast.js';
import withCurrentUser from '../../../hocs/with_current_user.js';
import { compose } from 'redux';

import ShowProjectUseCase from "../../../use_cases/projects/projects/show_project_use_case.js";

import Container from "../../../base/components/container.js";
import Routes from "../../routes.js";
import Analytics from "../../../utils/analytics/index.jsx";

import GalleryBlock from "./components/gallery_block.js";
import TitleBlock from "./components/title_block.js";
import KeyFactsBlock from "./components/key_facts_block.js";
import DisclaimerBlock from "../../../base/components/disclaimer_block.js";
import InvestFormBlock from "./components/invest_form_block.js";
import DescriptionBlock from "./components/description_block.js";
import AddressBlock from "./components/address_block.js";
import DeveloperBlock from "./components/developer_block.js";
import FinancingStructureBlock from "./components/financing_structure_block";
import DocumentsBlock from "./components/documents_block.js";

import { NotFoundErrorCode } from "../../../constants.js";
import BonusRatesBlock from "./components/bonus_rates_block.js.jsx";


const pageStyles = makeStyles(theme => ({
  root: {
    fontSize: "20px",
    lineHeight: "30px",
    fontWeight: theme.typography.fontWeightLight,
    paddingBottom: "74px",
    whiteSpace: "pre-line",
    ['@media (max-width: 768px)']: {
      fontSize: "14px",
      lineHeight: "18px",
    },
    "& a": {
      color: theme.palette.black,
      textDecoration: "none"
    },
    "& a:hover": {
      color: theme.palette.black,
      textDecoration: "underline"
    },
    "& h1": {
      fontSize: "45px",
      lineHeight: "45px",
      fontWeight: theme.typography.fontWeightMedium,
      textTransform: "uppercase",
      color: theme.palette.blueMain,
      textShadow: "0px 2px 6px rgba(0, 0, 0, 0.18)",
      marginTop: "0px",
      marginBottom: "12px",
      ['@media (max-width: 768px)']: {
        fontSize: "20px",
        lineHeight: "26px",
        marginBottom: "3px",
        textShadow: "none",
      },
    },
    "& h2": {
      fontSize: "30px",
      lineHeight: "1.2",
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.grayText,
      marginTop: "0px",
      marginBottom: "50px",
      ['@media (max-width: 768px)']: {
        fontSize: "14px",
        lineHeight: "20px",
        marginBottom: "21px",
        color: theme.palette.black,
      },
    },
    "& h3": {
      fontSize: "38px",
      lineHeight: "50px",
      fontWeight: theme.typography.fontWeightMedium,
      textTransform: "uppercase",
      marginTop: "0px",
      marginBottom: "24px",
      ['@media (max-width: 768px)']: {
        fontSize: "20px",
        lineHeight: "26px",
        marginBottom: "12px",
      },
    },
    "& h4": {
      fontSize: "24px",
      lineHeight: "28px",
      fontWeight: theme.typography.fontWeightMedium,
      textTransform: "uppercase",
      marginTop: "0px",
      marginBottom: "20px",
      ['@media (max-width: 768px)']: {
        color: theme.palette.bronze,
        fontSize: "12px",
        lineHeight: "16px",
        marginBottom: "13px",
      },
    },
  },
  gallery: {
    marginBottom: "60px",
    ['@media (max-width: 768px)']: {
      marginBottom: "26px",
    },
  },
  twoSidedBlock: {
    display: "flex",
    marginBottom: "50px",
    ['@media (max-width: 992px)']: {
      padding: "0px 24px",
      flexWrap: "wrap",
      marginBottom: "30px"
    },
  },
  leftSideBlock: {
    flexGrow: "2",
    flexBasis: "50%",
    ['@media (max-width: 992px)']: {
      flexBasis: "100%",
    },
    "& $keyFacts": {
      display: "block",
      ['@media (max-width: 992px)']: {
        display: "none",
      },
    }
  },
  separator: {
    flexGrow: "1",
    flexBasis: "10%",
    ['@media (max-width: 992px)']: {
      flexGrow: "0",
    },

  },
  rightSideBlock: {
    flexBasis: "35%",
    flexGrow: "2",
    ['@media (max-width: 992px)']: {
      flexBasis: "100%",
    },
    "& $keyFacts": {
      display: "none",
      ['@media (max-width: 992px)']: {
        display: "block",
      },
    }
  },
  title: {
    marginBottom: "0px",
    ['@media (max-width: 768px)']: {
      marginBottom: "-5px",
    },
  },
  keyFacts: {
    marginBottom: 0
  },
  investForm: {
    ['@media (max-width: 992px)']: {
      marginBottom: "37px",
    },
  },
  description: {
    marginBottom: "50px",
    ['@media (max-width: 768px)']: {
      marginBottom: "30px",
    },
  },
  address: {
    marginBottom: "50px",
    ['@media (max-width: 768px)']: {
      marginBottom: "38px",
    },
  },
  developer: {
    marginBottom: "50px",
    ['@media (max-width: 768px)']: {
      marginBottom: "34px",
    },
  },
  financingStructure: {
    marginBottom: "50px",
    ['@media (max-width: 768px)']: {
      marginBottom: "34px",
    },
  },
  documents: {
  },
  disclaimer: {
    marginBottom: "50px",
    ['@media (max-width: 992px)']: {
      margin: "0 8px 30px"
    }
  }
}));

const ProjectPage = (props) => {

  const [project, setProject] = useState(undefined);
  const [projectAbilityInfo, setProjectAbilityInfo] = useState({});
  const [participant, setParticipant] = useState(undefined);
  const [projectLoaded, setProjectLoaded] = useState(false);
  const [nominalInvestmentAmount, setNominalInvestmentAmount] = useState("100.0");

  useEffect(
    () => {
      const { slug } = props;
      if(!slug) {
        return;
      }
      ShowProjectUseCase.execute(slug)
      .then((result) => {
        const { data: { project, currentParticipant, nominalInvestmentAmount : nominal } } = result;
        const { data: { projectAbilityInfo } } = result;
        setProject(project);
        setProjectAbilityInfo(projectAbilityInfo);
        setParticipant(currentParticipant);
        setNominalInvestmentAmount(nominal);
        setProjectLoaded(true);
        Analytics.projectPage(project);
        window.location.href = project.xavinUrl;
      })
      .catch((result) => {
        const { history } = props;
        const { errorCode } = result;
        if(errorCode === NotFoundErrorCode) {
          history.replace(Routes.pageNotFound());
          return;
        }
        props.showServerErrorIfNeed(result);
      })
    },
    []
  )

  const classes = pageStyles();

  const { createInvestment, allowToSubscribe } = projectAbilityInfo;

  return (
    <div className = { classes.root } >
      {
        project &&
        <React.Fragment>
          <GalleryBlock className = { classes.gallery }
                        galleryImages = { project.galleryImages }/>
          <Container className = { classes.twoSidedBlock } >
            <div className = { classes.leftSideBlock } >
              <TitleBlock className = { classes.title }
                          project = { project } />
              <KeyFactsBlock className = { classes.keyFacts }
                             project = { project } />
            </div>
            { project.bonusRates.length == 0 &&
              <div>
                <div className = { classes.separator } />
                <BonusRatesBlock className = { classes.keyFacts } project = { project } />
              </div>
            }
            <div className = { classes.separator } />
            <div className = { classes.rightSideBlock } >
              <InvestFormBlock className = { classes.investForm }
                               allowToSubscribe = { allowToSubscribe }
                               project = { project }
                               participant = { participant }
                               nominalInvestmentAmount = { nominalInvestmentAmount }
                               formLoaded = { projectLoaded }
                               allowToInvest = { createInvestment } />
              <KeyFactsBlock className = { classes.keyFacts }
                             project = { project } />
            </div>
          </Container>
          <Container>
            <DisclaimerBlock className = { classes.disclaimer } />
          </Container>
          <DescriptionBlock className = { classes.description }
                            project = { project } />
          <AddressBlock className = { classes.address }
                        project = { project } />
          <DeveloperBlock className = { classes.developer }
                          project = { project } />
          <FinancingStructureBlock className = { classes.financingStructure }
                                   project = { project } />
          <DocumentsBlock className = { classes.documents }
                          project = { project } />
        </React.Fragment>
      }
    </div>
  );
}

export default compose(withRouter, withShowToast, withCurrentUser)(ProjectPage);
